// src/components/tasks/TaskModals.jsx
// Shared task UI: TaskDetailModal, TaskCreateModal, ConfirmActionModal, Avatar, helpers, constants.
// Used by the cover task sidebar and the manuscript goals panel.
import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import '../../styles/TaskModals.css';

// ── Constants ──────────────────────────────────────────────────────────────
export const PRIORITY_COLORS = { HIGH: '#ef4444', MEDIUM: '#f59e0b', LOW: '#10b981' };
export const PRIORITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH'];
export const STATUS_META = {
  unstarted:   { label: 'Unstarted',   bg: 'rgba(148,163,184,.18)', color: '#64748b' },
  in_progress: { label: 'In Progress', bg: 'rgba(217,164,6,.18)',   color: '#b45309' },
  completed:   { label: 'Completed',   bg: 'rgba(16,185,129,.18)',  color: '#059669' },
  cancelled:   { label: 'Cancelled',   bg: 'rgba(148,163,184,.18)', color: '#64748b' },
  rejected:    { label: 'Rejected',    bg: 'rgba(239,68,68,.18)',   color: '#dc2626' },
};

// ── Helpers ────────────────────────────────────────────────────────────────
export const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      return u?.id || u?._id || u?.userId || localStorage.getItem('user_id') || '';
    }
  } catch { /* noop */ }
  return localStorage.getItem('user_id') || '';
};

const _avatarColors = ['#7c3aed', '#f59e0b', '#10b981', '#0ea5e9', '#ef4444', '#ec4899', '#8b5cf6', '#14b8a6'];
const initialColor = (str = '') => {
  let h = 0; for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return _avatarColors[Math.abs(h) % _avatarColors.length];
};

export const Avatar = ({ url, name = '', size = 22, title }) => {
  if (url) return <img src={url} alt={name} title={title || name} className="ta-avatar" style={{ width: size, height: size }} />;
  const letter = (name || '?').charAt(0).toUpperCase();
  return (
    <span
      className="ta-avatar ta-avatar-fallback"
      title={title || name}
      style={{ width: size, height: size, background: initialColor(name), fontSize: Math.max(10, size * 0.45) }}
    >{letter}</span>
  );
};

export const fmtDate = (iso) => {
  if (!iso) return '';
  // A bare "2026-12-15" from a date input is a calendar date. new Date() reads
  // it as UTC midnight -- the evening of the 14th west of Greenwich -- so the
  // due date showed a day early. Read it as a local date instead.
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
  const d = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
export const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

export const taskStatus = (task) =>
  task.status || (task.completed ? 'completed' : (task.rejected ? 'rejected' : 'unstarted'));

export const taskAssignees = (task) =>
  task.assignees || (task.assigned_to ? [task.assigned_to] : []);

export const taskAssigneeNames = (task) =>
  task.assignee_usernames || (task.assigned_to_username ? [task.assigned_to_username] : []);

// ═══════════════════════════════════════════════════════════════════════════
//  CONFIRM ACTION MODAL — reusable warning popup for destructive actions
// ═══════════════════════════════════════════════════════════════════════════
export function ConfirmActionModal({ message, subtext, confirmLabel = 'Confirm', danger = true, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="ta-modal-backdrop" onClick={onCancel}>
      <div className="ta-modal ta-modal--confirm" onClick={e => e.stopPropagation()}>
        <div className="ta-modal-header">
          <h2 className="ta-modal-title">{message}</h2>
          {subtext && <p className="ta-confirm-subtext">{subtext}</p>}
        </div>
        <div className="ta-modal-actions">
          <button
            className={`ta-btn ${danger ? 'ta-btn-danger' : 'ta-btn-primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
          <button className="ta-btn ta-btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TASK DETAIL MODAL
// ═══════════════════════════════════════════════════════════════════════════
export function TaskDetailModal({
  task, team, avatars, isAuthor, currentUserId,
  // Picking a person replaces the current one (manuscript goals have one assignee)
  singleAssignee = false,
  // Optional features — pass null/undefined to hide
  canManuscript, hasManuscript, draftTitle, milestoneName,
  onClose, onStartTask, onMarkComplete, onCancelTask, onUnassignSelf,
  onUpdateTask, onAddComment, onDeleteTask, onGoToManuscript,
}) {
  const status = taskStatus(task);
  const isCompleted = status === 'completed';
  const isCancelled = status === 'cancelled';
  const assignees = taskAssignees(task);
  const assigneeNames = taskAssigneeNames(task);
  const isAssignee = assignees.map(String).includes(String(currentUserId));
  const myStatus = isAssignee ? ((task.assignee_statuses || {})[String(currentUserId)] || 'unstarted') : null;

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(task.name);
  const [editDetail, setEditDetail] = useState(task.detail || task.description || '');
  const [editPriority, setEditPriority] = useState(task.priority || 'MEDIUM');
  const [editDue, setEditDue] = useState(task.due_date ? task.due_date.slice(0, 10) : '');
  const [editAssignees, setEditAssignees] = useState(assignees);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [confirm, setConfirm] = useState(null); // { action, message, subtext, confirmLabel, danger }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirm) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, confirm]);

  const submitComment = async () => {
    if (!comment.trim() || posting || !onAddComment) return;
    setPosting(true);
    const ok = await onAddComment(comment.trim());
    setPosting(false);
    if (ok) setComment('');
  };

  const submitUpdate = async () => {
    const patch = {
      name: editName,
      detail: editDetail,
      priority: editPriority,
      due_date: editDue || null,
      assignees: editAssignees,
    };
    const ok = await onUpdateTask(patch);
    if (ok) setEditing(false);
  };

  const toggleEditAssignee = (uid) => {
    setEditAssignees(prev => prev.includes(uid)
      ? prev.filter(x => x !== uid)
      : (singleAssignee ? [uid] : [...prev, uid]));
  };

  const handleConfirm = async () => {
    const action = confirm?.action;
    setConfirm(null);
    if (action === 'delete') { await onDeleteTask?.(); }
    else if (action === 'cancel') { await onCancelTask?.(); }
    else if (action === 'unassign') { await onUnassignSelf?.(); }
  };

  return (
    <div className="ta-modal-backdrop" onClick={onClose}>
      <div className="ta-modal" onClick={e => e.stopPropagation()}>
        <button className="ta-modal-close" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </button>

        <div className="ta-modal-header">
          {editing ? (
            <input className="ta-edit-title" value={editName} onChange={e => setEditName(e.target.value)} />
          ) : (
            <h2 className="ta-modal-title">{task.name}</h2>
          )}
        </div>

        <div className="ta-modal-body">
          {/* LEFT — details */}
          <div className="ta-modal-left">
            <div className="ta-fields">
              <div className="ta-field">
                <span className="ta-label">Status:</span>
                <span className="ta-status-pill" style={{ background: STATUS_META[status]?.bg, color: STATUS_META[status]?.color }}>
                  {STATUS_META[status]?.label || status}
                </span>
              </div>
              <div className="ta-field">
                <span className="ta-label">Priority:</span>
                {editing ? (
                  <select value={editPriority} onChange={e => setEditPriority(e.target.value)} className="ta-input">
                    {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                ) : (
                  <span className="ta-priority-pill" style={{ background: PRIORITY_COLORS[task.priority] + '33', color: PRIORITY_COLORS[task.priority] }}>
                    {task.priority}
                  </span>
                )}
              </div>
              <div className="ta-field">
                <span className="ta-label">Due Date:</span>
                {editing ? (
                  <input type="date" className="ta-input" value={editDue} onChange={e => setEditDue(e.target.value)} />
                ) : (
                  <span className="ta-value">{task.due_date ? fmtDate(task.due_date) : '—'}</span>
                )}
              </div>
            </div>

            <div className="ta-fields">
              <div className="ta-field">
                <span className="ta-label">Requestor:</span>
                <span className="ta-value ta-with-avatar">
                  <Avatar url={avatars[task.created_by]} name={task.requestor_username || ''} size={22} />
                  <span>{task.requestor_username || 'Unknown'}</span>
                </span>
              </div>
              {draftTitle && (
                <div className="ta-field">
                  <span className="ta-label">Book:</span>
                  <span className="ta-value">{draftTitle}</span>
                </div>
              )}
              {milestoneName && (
                <div className="ta-field">
                  <span className="ta-label">Milestone:</span>
                  <span className="ta-value">{milestoneName}</span>
                </div>
              )}
            </div>

            {/* Feedback banner when rejected */}
            {status === 'rejected' && task.feedback && (
              <div className="ta-feedback-banner">
                <span className="ta-feedback-label">Feedback:</span>
                <span className="ta-feedback-text">{task.feedback}</span>
              </div>
            )}

            <div className="ta-field ta-field-assignees">
              <span className="ta-label">Assigned to:</span>
              {editing ? (
                <div className="ta-assignee-chips">
                  {team.map(u => {
                    const sel = editAssignees.includes(u.id);
                    return (
                      <button key={u.id} type="button" className={`ta-chip${sel ? ' ta-chip-active' : ''}`} onClick={() => toggleEditAssignee(u.id)}>
                        <Avatar url={avatars[u.id]} name={u.username} size={18} />
                        <span>{u.username}</span>
                      </button>
                    );
                  })}
                </div>
              ) : assignees.length === 0 ? (
                <span className="ta-value">Unassigned</span>
              ) : isAuthor ? (
                /* Author sees each assignee with their individual status */
                <div className="ta-assignee-status-list">
                  {assignees.map((aid, i) => {
                    const aStatus = (task.assignee_statuses || {})[aid] || 'unstarted';
                    return (
                      <div key={aid} className="ta-assignee-status-row">
                        <Avatar url={avatars[aid]} name={assigneeNames[i] || ''} size={22} />
                        <span className="ta-assignee-name">{assigneeNames[i] || 'Unknown'}</span>
                        <span className="ta-status-pill ta-status-pill--sm" style={{ background: STATUS_META[aStatus]?.bg, color: STATUS_META[aStatus]?.color }}>
                          {STATUS_META[aStatus]?.label || aStatus}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="ta-avatar-stack">
                  {assignees.map((aid, i) => (
                    <Avatar key={aid} url={avatars[aid]} name={assigneeNames[i] || ''} size={26} title={assigneeNames[i]} />
                  ))}
                </span>
              )}
            </div>

            <div className="ta-detail">
              <div className="ta-label ta-label-block">Detail:</div>
              {editing ? (
                <textarea className="ta-input ta-textarea" value={editDetail} onChange={e => setEditDetail(e.target.value)} rows={6} />
              ) : (
                <p className="ta-detail-text">{task.detail || task.description || '—'}</p>
              )}
            </div>
          </div>

          {/* RIGHT — comments */}
          <div className="ta-modal-right">
            <div className="ta-comments-header">Comment History</div>
            <div className="ta-comments-list">
              {(task.comments || []).length === 0 && (
                <div className="ta-comments-empty">No comments yet.</div>
              )}
              {(task.comments || []).map(c => {
                const isMe = String(c.user_id) === String(currentUserId);
                return (
                  <div key={c._id} className="ta-comment">
                    <div className="ta-comment-meta">
                      <span className="ta-comment-author">{isMe ? 'Me' : (c.username || 'User')}</span>
                      <span className="ta-comment-time">{fmtDateTime(c.created_at)}</span>
                    </div>
                    <div className="ta-comment-text">{c.text}</div>
                  </div>
                );
              })}
            </div>
            {onAddComment && (
              <div className="ta-comment-input-row">
                <textarea
                  className="ta-comment-input"
                  placeholder="Start type here…"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                  rows={2}
                />
                <button className="ta-comment-send" onClick={submitComment} disabled={!comment.trim() || posting} aria-label="Send">
                  <SendIcon fontSize="small" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="ta-modal-actions">
          {onGoToManuscript && hasManuscript && (
            <button className="ta-btn ta-btn-primary" onClick={onGoToManuscript} disabled={!canManuscript} title={!canManuscript ? 'No access to manuscript' : 'Open manuscript'}>
              Go to Manuscript
            </button>
          )}

          {editing ? (
            <>
              <button className="ta-btn ta-btn-primary" onClick={submitUpdate}>Save</button>
              <button className="ta-btn ta-btn-ghost" onClick={() => setEditing(false)}>Cancel Edit</button>
            </>
          ) : (
            <>
              {/* Author-only actions */}
              {isAuthor && onUpdateTask && (
                <button className="ta-btn ta-btn-primary" onClick={() => setEditing(true)}>Update Task</button>
              )}
              {isAuthor && onCancelTask && !isCancelled && (
                <button className="ta-btn ta-btn-ghost" onClick={() => setConfirm({
                  action: 'cancel',
                  message: 'Cancel this task?',
                  subtext: 'This will cancel the task for all assignees.',
                  confirmLabel: 'Cancel Task',
                  danger: false,
                })}>Cancel Task</button>
              )}
              {isAuthor && onDeleteTask && (
                <button className="ta-btn ta-btn-danger" onClick={() => setConfirm({
                  action: 'delete',
                  message: 'Delete this task permanently?',
                  subtext: 'This will delete the task and all its history. This cannot be undone.',
                  confirmLabel: 'Delete',
                  danger: true,
                })}>Delete</button>
              )}

              {/* Assignee actions */}
              {!isAuthor && isAssignee && !isCancelled && myStatus === 'unstarted' && onStartTask && (
                <button className="ta-btn ta-btn-primary" onClick={onStartTask}>Start Task</button>
              )}
              {!isAuthor && isAssignee && !isCompleted && !isCancelled && myStatus === 'in_progress' && onMarkComplete && (
                <button className="ta-btn ta-btn-primary" onClick={onMarkComplete}>Mark as Complete</button>
              )}
              {!isAuthor && isAssignee && onUnassignSelf && (
                <button className="ta-btn ta-btn-ghost" onClick={() => setConfirm({
                  action: 'unassign',
                  message: 'Unassign yourself from this task?',
                  subtext: 'The author will be notified and can reassign someone else.',
                  confirmLabel: 'Unassign Me',
                  danger: false,
                })}>Unassign Self</button>
              )}
            </>
          )}

          <button className="ta-btn ta-btn-link" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Confirmation popup — rendered inside the modal's stacking context */}
      {confirm && (
        <ConfirmActionModal
          message={confirm.message}
          subtext={confirm.subtext}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TASK CREATE MODAL — pop-up for author to set up a new task
// ═══════════════════════════════════════════════════════════════════════════
export function TaskCreateModal({ team, avatars, title = 'New Task', subtitle, singleAssignee = false, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [detail, setDetail] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [due, setDue] = useState('');
  const [invitationExpiration, setInvitationExpiration] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const toggleAssignee = (uid) => {
    setAssignees(prev => prev.includes(uid)
      ? prev.filter(x => x !== uid)
      : (singleAssignee ? [uid] : [...prev, uid]));
  };

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    await onCreate({
      name: name.trim(),
      detail,
      priority,
      due_date: due || null,
      invitation_expiration_date: invitationExpiration || null,
      assignees,
      status: 'unstarted',
    });
    setSaving(false);
  };

  return (
    <div className="ta-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className="ta-modal ta-modal--narrow" onClick={e => e.stopPropagation()}>
        <button className="ta-modal-close" onClick={onClose} aria-label="Close" disabled={saving}>
          <CloseIcon fontSize="small" />
        </button>

        <div className="ta-modal-header">
          <h2 className="ta-modal-title">{title}</h2>
          {subtitle && <div className="ta-modal-subtitle">{subtitle}</div>}
        </div>

        <div className="ta-modal-body ta-modal-body--single">
          <div className="ta-modal-left">
            <div className="ta-create-field">
              <label className="ta-label ta-label-block">Title</label>
              <input
                className="ta-input ta-input-full"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Task title…"
                autoFocus
              />
            </div>

            <div className="ta-fields">
              <div className="ta-field">
                <span className="ta-label">Priority:</span>
                <select className="ta-input" value={priority} onChange={e => setPriority(e.target.value)}>
                  {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="ta-field">
                <span className="ta-label">Due Date:</span>
                <input type="date" className="ta-input" value={due} onChange={e => setDue(e.target.value)} />
              </div>
              <div className="ta-field">
                <span className="ta-label">Invite Expires:</span>
                <input type="date" className="ta-input" value={invitationExpiration} onChange={e => setInvitationExpiration(e.target.value)} />
              </div>
            </div>

            <div className="ta-create-field">
              <label className="ta-label ta-label-block">Assigned to</label>
              <div className="ta-assignee-chips">
                {team.length === 0 && <span className="ta-value">No accepted collaborators available</span>}
                {team.map(u => {
                  const sel = assignees.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={`ta-chip${sel ? ' ta-chip-active' : ''}`}
                      onClick={() => toggleAssignee(u.id)}
                    >
                      <Avatar url={avatars[u.id]} name={u.username} size={18} />
                      <span>{u.username}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="ta-create-field">
              <label className="ta-label ta-label-block">Detail</label>
              <textarea
                className="ta-input ta-textarea"
                value={detail}
                onChange={e => setDetail(e.target.value)}
                placeholder="What does this task involve?"
                rows={5}
              />
            </div>
          </div>
        </div>

        <div className="ta-modal-actions">
          <button className="ta-btn ta-btn-primary" onClick={submit} disabled={!name.trim() || saving}>
            {saving ? 'Creating…' : 'Create Task'}
          </button>
          <button className="ta-btn ta-btn-link" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  COMPACT TASK ROW — clickable row used in lists (sidebar / panels)
// ═══════════════════════════════════════════════════════════════════════════
export function TaskRow({ task, avatars, onClick, className = '' }) {
  const status = taskStatus(task);
  const cancelled = status === 'cancelled';
  const done = status === 'completed';
  const rejected = status === 'rejected';
  const cls = `ct-task ct-task-clickable${done ? ' ct-done' : ''}${rejected ? ' ct-rejected' : ''}${cancelled ? ' ct-cancelled' : ''} ${className}`;
  const assignees = taskAssignees(task);
  const usernames = taskAssigneeNames(task);
  return (
    <div
      className={cls}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    >
      <div className="ct-task-body">
        <span className="ct-task-name">{task.name}</span>
        <div className="ct-task-meta">
          <span className="ta-status-pill" style={{ background: STATUS_META[status]?.bg, color: STATUS_META[status]?.color }}>
            {STATUS_META[status]?.label || status}
          </span>
          <span className="ct-priority" style={{ color: PRIORITY_COLORS[task.priority] || '#999' }}>
            {task.priority}
          </span>
          {task.due_date && <span className="ta-due">Due {fmtDate(task.due_date)}</span>}
          {assignees.length > 0 && (
            <span className="ta-avatar-stack">
              {assignees.slice(0, 4).map((aid, i) => (
                <Avatar key={aid} url={avatars[aid]} name={usernames[i] || ''} size={20} />
              ))}
              {assignees.length > 4 && <span className="ta-avatar-more">+{assignees.length - 4}</span>}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
